/**
 * Types and constants for PO Lines Tab components
 */
import { PurchaseOrderLine } from '../../../../lib/api';

// CR-3406: Column configuration for visibility management
// CR-3846: Added order field for column reordering
export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  minWidth: string;
  order: number;  // CR-3846: Column display order
}

// CR-3850: Added additional columns from old platform
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'acknowledge_status', label: 'Ack Status', visible: true, sortable: true, minWidth: '170px', order: 0 },
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '100px', order: 1 },
  { id: 'is_critical', label: 'Critical', visible: true, sortable: true, minWidth: '80px', order: 2 },
  { id: 'on_hold', label: 'On Hold', visible: true, sortable: true, minWidth: '100px', order: 3 },
  { id: 'hold_reason', label: 'Hold Reason', visible: true, sortable: false, minWidth: '120px', order: 4 },
  { id: 'next_action', label: 'Next Action', visible: true, sortable: true, minWidth: '120px', order: 5 },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, minWidth: '140px', order: 6 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 7 },
  { id: 'customer_name', label: 'Customer', visible: true, sortable: true, minWidth: '150px', order: 8 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, minWidth: '220px', order: 9 },
  { id: 'supplier_group_name', label: 'Supplier Group', visible: false, sortable: true, minWidth: '150px', order: 10 },  // CR-3850
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '160px', order: 12 },
  { id: 'description', label: 'Description', visible: true, sortable: true, minWidth: '200px', order: 13 },
  { id: 'quantity', label: 'Qty Ordered', visible: true, sortable: true, minWidth: '140px', order: 14 },
  { id: 'received_qty', label: 'Qty Delivered', visible: true, sortable: true, minWidth: '160px', order: 15 },
  { id: 'outstanding_qty', label: 'Qty O/S', visible: true, sortable: true, minWidth: '140px', order: 16 },
  { id: 'pct_delivered', label: '% Delivered', visible: true, sortable: true, minWidth: '130px', order: 17 },
  { id: 'unit_price', label: 'Unit Price', visible: true, sortable: true, minWidth: '140px', order: 18 },
  { id: 'total_price', label: 'Total', visible: true, sortable: true, minWidth: '110px', order: 19 },
  { id: 'currency', label: 'Currency', visible: false, sortable: true, minWidth: '100px', order: 20 },
  // CR-4694 round 2 rework: 'Customer Required Date' (delivery_date) column
  // removed from the table view per spec — the field was being populated by
  // the import path that should have been writing to MRP/ERP Scheduled, so
  // its values were misleading. The delivery_date model field is unchanged
  // (OTIF, on-time, and many other downstream calculations still depend on
  // it), only the surface in the column picker is removed.
  { id: 'supplier_promise_date', label: 'Latest Supplier Promise Date', visible: true, sortable: true, minWidth: '220px', order: 22 },
  { id: 'buyer_id', label: 'Buyer ID', visible: true, sortable: true, minWidth: '100px', order: 23 },
  { id: 'batch_serial_no', label: 'Batch/Serial No', visible: false, sortable: true, minWidth: '140px', order: 24 },
  { id: 'ppv', label: 'PPV', visible: false, sortable: true, minWidth: '100px', order: 25 },
  { id: 'otif_overridden', label: 'OTIF Overridden', visible: false, sortable: true, minWidth: '130px', order: 26 },
  { id: 'packing_note_number', label: 'PnR Note No', visible: false, sortable: true, minWidth: '120px', order: 27 },
  { id: 'order_date', label: 'Order Date', visible: false, sortable: true, minWidth: '120px', order: 28 },
  { id: 'last_received_date', label: 'Last Received', visible: false, sortable: true, minWidth: '120px', order: 29 },
  { id: 'updated_at', label: 'Update Time', visible: false, sortable: true, minWidth: '150px', order: 30 },
  { id: 'required_date_change_count', label: 'Req Date Changes', visible: false, sortable: true, minWidth: '130px', order: 31 },
  { id: 'promised_date_change_count', label: 'Promise Date Changes', visible: false, sortable: true, minWidth: '150px', order: 32 },
  { id: 'pdf_attached_count', label: 'PDF Attached', visible: false, sortable: true, minWidth: '100px', order: 33 },
  { id: 'material_cert_count', label: 'Mat Certs', visible: false, sortable: true, minWidth: '100px', order: 34 },
  { id: 'contract_unit_price', label: 'Contract Unit Price', visible: false, sortable: true, minWidth: '140px', order: 35 },
  { id: 'contract_lot_price', label: 'Contract Lot Price', visible: false, sortable: true, minWidth: '140px', order: 36 },
  { id: 'contract_price', label: 'Contract Price', visible: false, sortable: true, minWidth: '130px', order: 37 },
  { id: 'drawing_number', label: 'Drawing No', visible: false, sortable: true, minWidth: '120px', order: 38 },
  { id: 'drawing_version', label: 'Drawing Issue', visible: false, sortable: true, minWidth: '100px', order: 39 },
  { id: 'outstanding_value', label: 'O/S Value', visible: false, sortable: true, minWidth: '120px', order: 40 },
  { id: 'converted_unit_price', label: 'Conv. Unit Price', visible: false, sortable: true, minWidth: '130px', order: 41 },
  { id: 'converted_total_value', label: 'Conv. Total', visible: false, sortable: true, minWidth: '120px', order: 42 },
  { id: 'converted_outstanding_value', label: 'Conv. O/S Value', visible: false, sortable: true, minWidth: '130px', order: 43 },
  { id: 'original_promise_date', label: 'Agreed Date (ODD)', visible: false, sortable: true, minWidth: '140px', order: 44 },
  // CR-4694 round 2 rework: MRP/ERP Scheduled now visible by default
  // since it replaces the removed Customer Required Date column as the
  // primary import-driven date surface.
  { id: 'mrp_scheduled_date', label: 'MRP/ERP Scheduled', visible: true, sortable: true, minWidth: '150px', order: 45 },
  { id: 'priority', label: 'Priority', visible: false, sortable: true, minWidth: '100px', order: 46 },
  { id: 'exchange_rate', label: 'Exchange Rate', visible: false, sortable: true, minWidth: '120px', order: 47 },  // CR-4679
];

export const COLUMN_STORAGE_KEY = 'po_lines_column_config';

// CR-4716: Commercial/internal fields restricted to customer users only.
// Supplier users must not see these in the table, column picker, or filters.
// Converted/exchange-rate fields are the customer's reporting-currency view —
// suppliers must only see the original transaction-currency price.
export const SUPPLIER_RESTRICTED_COLUMNS = [
  'buyer_id',
  'ppv',
  'otif_overridden',
  'contract_unit_price',
  'contract_lot_price',
  'contract_price',
  'converted_unit_price',
  'converted_total_value',
  'converted_outstanding_value',
  'exchange_rate',
];

// Filter state interface
// CR-3413: Removed dateFrom/dateTo (Delivery From/To), combined status filters
export interface POLinesFilters {
  poNumber: string;
  lineNumber: string;
  supplier: string;
  supplierSearch: string;  // CR-3413: For searchable supplier dropdown
  supplierGroup: string;
  customer: string;
  customerSearch: string;  // CR-3413: For searchable customer/site dropdown
  partNumber: string;
  description: string;
  status: string;  // CR-3413: Combined status filter (replaces separate status and acknowledgeStatus)
  oddFrom: string;
  oddTo: string;
  on_hold: string;
  buyerId: string;
  batchSerialNo: string;
  otifOverridden: string;
  nextAction: string;
  ppv: string;
  commodityGroup: string;
  commodityType: string;
}

// CR-3413: Removed dateFrom/dateTo, acknowledgeStatus (now combined into status)
export const DEFAULT_FILTERS: POLinesFilters = {
  poNumber: '',
  lineNumber: '',
  supplier: '',
  supplierSearch: '',
  supplierGroup: '',
  customer: '',
  customerSearch: '',
  partNumber: '',
  description: '',
  status: '',
  oddFrom: '',
  oddTo: '',
  on_hold: '',
  buyerId: '',
  batchSerialNo: '',
  otifOverridden: '',
  nextAction: '',
  ppv: '',
  commodityGroup: '',
  commodityType: '',
};

// CR-3413: Unified status options combining Status and Acknowledge Status
// Note: 'changed' maps to change_proposed_supplier + change_proposed_customer in the backend
// Note: 'on_hold' maps to the on_hold boolean flag (not an acknowledge_status)
// Note: 'closed' maps to PO line status 'received'
export const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'changed', label: 'Changed' },
  { value: 'change_declined', label: 'Change Declined' },
  { value: 'wip', label: 'WIP' },
  { value: 'declined', label: 'Declined' },
  { value: 'despatched', label: 'Despatched' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

// Filter options from API
export interface FilterOptions {
  suppliers: Array<{ id: number; name: string }>;
  customers: Array<{ id: number; name: string; group?: number | null }>;
  commodity_groups: Array<{ id: number; name: string }>;
  commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
  supplier_groups?: Array<{ id: number; name: string }>;
}

// Acknowledge status configuration
export interface StatusConfig {
  label: string;
  className: string;
}

export const ACKNOWLEDGE_STATUS_CONFIG: Record<string, StatusConfig> = {
  new: { label: 'New', className: 'bg-blue-500 text-white' },
  declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
  change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
  change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
  wip: { label: 'WIP', className: 'bg-green-500 text-white' },
  accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
  despatched: { label: 'Despatched', className: 'bg-gray-200 text-gray-900' },
  change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  closed: { label: 'Closed', className: 'bg-gray-400 text-white' },
};

// Next action result type
export interface NextActionResult {
  text: string;
  className: string;
}

// Re-export PurchaseOrderLine for convenience
export type { PurchaseOrderLine };
